import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, Badge, Tabs, EmptyState, Button, Alert } from '../components/design-system';
import { Layers, ArrowRight, Sparkles, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

export const ClustersPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('active');
  const [reclusterLoading, setReclusterLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { loadClusters(); }, [page, statusFilter]);

  const loadClusters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '30');
      params.set('status', statusFilter);
      const res = await api.getClusters(params.toString());
      setClusters(res.clusters || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleGenerate = async (e, clusterId) => {
    e.stopPropagation();
    try {
      const res = await api.generateFromCluster(clusterId);
      if (res.article) navigate(`/articles/${res.article.id}`);
    } catch (err) { alert(err.message); }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Story Clusters</h2>
          <p className="text-sm text-gray-500">{total} {statusFilter} clusters</p>
        </div>
        {isAdmin && (
          <Button icon={RefreshCw} variant="secondary" size="sm" loading={reclusterLoading}
            onClick={async () => {
              if (!confirm('Reset all active clusters and re-cluster from scratch? Run pipeline afterwards to rebuild.')) return;
              setReclusterLoading(true); setMessage(null);
              try {
                const res = await api.reclusterAll();
                setMessage({ type: 'success', text: res.message });
                loadClusters();
              } catch (err) { setMessage({ type: 'error', text: err.message }); }
              finally { setReclusterLoading(false); }
            }}>
            Re-cluster All
          </Button>
        )}
      </div>
      {message && <Alert type={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Tabs
        tabs={[
          { id: 'active', label: 'Active' },
          { id: 'stale', label: 'Stale' },
          { id: 'archived', label: 'Archived' },
        ]}
        activeTab={statusFilter}
        onChange={(id) => { setStatusFilter(id); setPage(1); }}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : clusters.length === 0 ? (
        <EmptyState icon={Layers} title="No clusters" description="Clusters are automatically created when related news items are detected." />
      ) : (
        <div className="grid gap-3">
          {clusters.map((cluster) => {
            const topics = safeParseArray(cluster.topics);
            return (
              <Card key={cluster.id} hover onClick={() => navigate(`/clusters/${cluster.id}`)} className="!p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{cluster.title}</h3>
                    {cluster.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{cluster.summary}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge variant="primary" dot>{cluster.source_count} sources</Badge>
                      <Badge variant="info">{cluster.item_count} items</Badge>
                      {topics.slice(0, 3).map((t, i) => <Badge key={i}>{t}</Badge>)}
                      <span className="text-xs text-gray-400">{formatTimeAgo(cluster.last_updated_at)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-lg font-bold text-primary-600">{Math.round((cluster.importance_score || 0) * 100)}%</div>
                      <div className="text-[10px] text-gray-400">importance</div>
                    </div>
                    {cluster.source_count >= 2 && (
                      <Button size="sm" variant="secondary" icon={Sparkles}
                        onClick={(e) => handleGenerate(e, cluster.id)}>Generate</Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};

function safeParseArray(str) { try { return JSON.parse(str || '[]'); } catch { return []; } }

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
